import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { finalize, map, Observable, of, shareReplay, throwError } from 'rxjs';
import { AUTH_KEYS } from '../interfaces/auth-response';
import { BYPASS_AUTH } from '../interceptors/auth.interceptor';
import { EnvironmentService } from './environment.service';

interface CiamRefreshResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

@Injectable({ providedIn: 'root' })
export class CiamRefreshService {
  private readonly http = inject(HttpClient);
  private readonly environmentService = inject(EnvironmentService);
  private refreshInProgress$: Observable<string> | null = null;

  getToken(): Observable<string | null> {
    const accessToken = localStorage.getItem(AUTH_KEYS.ACCESS_TOKEN);

    if (!accessToken || !this.isTokenExpired()) {
      return of(accessToken);
    }

    const refreshToken = localStorage.getItem(AUTH_KEYS.REFRESH_TOKEN);
    if (!refreshToken) {
      return throwError(() => new Error('Refresh token non disponibile.'));
    }

    return this.refresh(refreshToken);
  }

  private refresh(refreshToken: string): Observable<string> {
    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }

    const baseUrl = this.environmentService.ciamBaseUrl().trim().replace(/\/+$/, '');
    if (!baseUrl) {
      return throwError(() => new Error(
        "CIAM base URL non configurato per l'ambiente corrente."
      ));
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: this.environmentService.ciamTokenAuthHeader()
    });
    const context = new HttpContext().set(BYPASS_AUTH, true);
    const body = {
      refresh_token: refreshToken,
      grant_type: 'refresh_token'
    };

    this.refreshInProgress$ = this.http
      .post<CiamRefreshResponse>(`${baseUrl}/refresh`, body, { headers, context })
      .pipe(
        map(response => {
          if (!response.access_token) {
            throw new Error('Access token non presente nella risposta di refresh.');
          }

          this.saveTokens(response);
          return response.access_token;
        }),
        finalize(() => this.refreshInProgress$ = null),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.refreshInProgress$;
  }

  private isTokenExpired(): boolean {
    const expiresAt = Number(localStorage.getItem(AUTH_KEYS.EXPIRES_AT));
    const expirationBuffer = 15 * 1000;
    return !expiresAt || Date.now() + expirationBuffer >= expiresAt;
  }

  private saveTokens(response: CiamRefreshResponse): void {
    localStorage.setItem(AUTH_KEYS.ACCESS_TOKEN, response.access_token);

    if (response.refresh_token) {
      localStorage.setItem(AUTH_KEYS.REFRESH_TOKEN, response.refresh_token);
    }

    const expiresInSeconds = Number(response.expires_in) || 3600;
    localStorage.setItem(
      AUTH_KEYS.EXPIRES_AT,
      (Date.now() + expiresInSeconds * 1000).toString()
    );
  }
}
